import { io as socketIO } from 'socket.io-client'
import { REST_API } from 'src/constants/DefaultValues'

/**
 * Optional handshake token for clients that store JWT in localStorage.
 * Primary auth remains httpOnly cookie via withCredentials (not readable from JS).
 */
function getOptionalHandshakeToken() {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage.getItem('accessToken') || null
  } catch (e) {
    return null
  }
}

function resolveSocketTarget() {
  let origin = REST_API.replace(/\/$/, '')
  let path = '/socket.io'

  try {
    const url = new URL(REST_API)
    origin = url.origin
    const basePath = url.pathname.replace(/\/$/, '')
    // If API is mounted under /backend (common Namecheap reverse-proxy),
    // Socket.IO must use /backend/socket.io — not namespace "/backend".
    if (basePath && basePath !== '/') {
      path = `${basePath}/socket.io`
    }
  } catch (e) {
    // keep defaults
  }

  return { origin, path }
}

/** Single shared Socket.IO client for the whole dashboard (avoids Chrome 6-conn starvation). */
let sharedSocket = null
let sharedOrigin = null
let sharedPath = null

/**
 * Reuse one Socket.IO connection app-wide.
 * Prefer WebSocket so long-polling does not block XHR on the same host.
 * Callers must only `.off()` their listeners on unmount — do not `.disconnect()`.
 */
export function getSocketConnection() {
  const { origin, path } = resolveSocketTarget()

  if (sharedSocket) {
    return { socket: sharedSocket, origin: sharedOrigin, path: sharedPath }
  }

  const token = getOptionalHandshakeToken()

  sharedOrigin = origin
  sharedPath = path
  sharedSocket = socketIO(origin, {
    path,
    withCredentials: true,
    ...(token ? { auth: { token } } : {}),
    // WebSocket ONLY. Polling fallback was re-creating Chrome's 6-connection stall
    // (long-poll holds slots → API XHR stays Pending ~10–15s). If WS is blocked by
    // the host/proxy, realtime fails but REST APIs stay fast (pages have HTTP fallbacks).
    transports: ['websocket'],
    upgrade: false,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 2000,
  })

  return { socket: sharedSocket, origin: sharedOrigin, path: sharedPath }
}

/** Tear down the shared socket (e.g. on logout). */
export function disconnectSocket() {
  if (sharedSocket) {
    sharedSocket.removeAllListeners()
    sharedSocket.disconnect()
    sharedSocket = null
    sharedOrigin = null
    sharedPath = null
  }
}
